// pages/qna/QnADetailPage.tsx
import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {deleteQnA, getQnADetail, QnADetailDto} from '../../api/deskApi';
import FileListComponent from '../../components/common/FileListComponent';

const QnADetailPage = () => {
    const navigate = useNavigate();
    const {id} = useParams<{ id: string }>();
    const [qna, setQna] = useState<QnADetailDto | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (id) {
            fetchQnADetail(id);
        }
    }, [id]);

    const fetchQnADetail = async (qnaCode: string) => {
        try {
            setIsLoading(true);
            const response = await getQnADetail(qnaCode);

            if (response.data?.code === "00" && response.data.data) {
                setQna(response.data.data);
            } else {
                alert('문의 내역을 불러올 수 없습니다.');
                navigate('/desk/qna');
            }
        } catch (error) {
            console.error('Q&A 상세 조회 오류:', error);
            alert('문의 내역을 불러오는 중 오류가 발생했습니다.');
            navigate('/desk/qna');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!qna) return;

        if (!window.confirm('정말 삭제하시겠습니까?')) {
            return;
        }

        try {
            const response = await deleteQnA(qna.qnaCode);

            if (response.data?.code === "00") {
                alert('삭제되었습니다.');
                navigate('/desk/qna', { replace: true });
            } else {
                alert(response.data?.message || '삭제에 실패했습니다.');
            }
        } catch (error) {
            console.error('Q&A 삭제 오류:', error);
            alert('삭제 중 오류가 발생했습니다.');
        }
    };

    const handleEdit = () => {
        if (!qna) return;
        navigate(`/desk/qna/edit/${qna.qnaCode}`);
    };

    // 카테고리 텍스트 변환
    const getCategoryText = (category: string) => {
        const categoryMap: { [key: string]: string } = {
            'MEMBER': '회원',
            'SERVICE': '서비스',
            'PAYMENT': '선불',
            'BILLING': '결제',
            'ETC': '기타',
        };
        return categoryMap[category] || category;
    };

    // 상태 표시 변환
    const getStatusText = (status: string) => {
        switch (status) {
            case 'PENDING':
                return '접수';
            case 'PROCESSING':
                return '처리중';
            case 'COMPLETED':
                return '완료';
            default:
                return status;
        }
    };

    if (isLoading) {
        return (
            <main className="container type_gray">
                <div className="inner-box">
                    <p style={{textAlign: 'center', padding: '50px 0'}}>로딩 중...</p>
                </div>
            </main>
        );
    }

    if (!qna) {
        return null;
    }

    const isCompleted = qna.status === 'COMPLETED';

    // 파일 목록 (예시 - 실제로는 API에서 받아와야 함)
    const files = qna.questionFileList && qna.questionFileList.length > 0 ? qna.questionFileList : [];

    return (
        <>
            <div className="bottom-btn">
                <button type="button" className="btn back" onClick={() => navigate('/desk/qna')}>
                    목록
                </button>
            </div>

            <main className="container type_gray">
                <section className="boardHeadline bg-white">
                    <div className="inner">
                        <div className="inquiry-box">
                            <p className="cate">문의분류 : {getCategoryText(qna.category || '')}</p>
                            <p className="txt">
                                <span className="color">Q.</span>
                                {qna.questionTitle}
                            </p>
                            <p className="date">{qna.regDate?.split('T')[0] || qna.regDate}</p>
                            <div className={`status ${isCompleted ? 'on' : ''}`}>
                                {getStatusText(qna.status)}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="bg-white no-gap">
                    <div className="inner-box">
                        <div className="boardDetail qna">
                            <p className="txt">{qna.questionContent}</p>
                        </div>
                        {/* 파일 목록 표시 */}
                    </div>
                    <FileListComponent files={files}/>
                </section>



                <section className="no-gap">
                    <div className="inner">
                        <div className="board-btn-wrap">
                            <button type="button" className="delete" onClick={handleDelete}>
                                삭제
                            </button>
                            {!isCompleted && (
                                <button type="button" className="edit" onClick={handleEdit}>
                                    문의 수정
                                </button>
                            )}
                        </div>
                    </div>
                </section>


                {qna.answerContent && (
                    <section className="bg-white no-gap">
                        <div className="inner-box">
                            <div className="reply">
                                <h3 className="txt">
                                    <span className="color">A.</span>
                                    {qna.answerTitle}
                                </h3>
                                <p className="date">
                                    <span>작성일</span>
                                    {qna.answeredDate?.split('T')[0] || qna.answeredDate}
                                </p>
                            </div>
                            <div className="boardDetail qna">
                                <p className="txt" style={{whiteSpace: 'pre-wrap'}}>
                                    {qna.answerContent}
                                </p>
                            </div>
                        </div>
                    </section>
                )}
            </main>
        </>
    );
};

export default QnADetailPage;